/**
 * One edge node, in full — and the management actions the fleet view has no
 * room for: rename it, move it to a different center, disable it, or forget it.
 *
 * Two things this page is careful to say plainly, because both are easy to get
 * wrong from a panel:
 *
 * * **A save here is immediate.** Unlike a center edit, editing a node writes
 *   straight to the shared registry — there is no plan to preview. Disabling a
 *   node stops its heartbeats being accepted the moment you save.
 * * **Deleting is not disabling.** A deleted node that is still running in a
 *   hospital will re-enrol with a fresh token and reappear. To stop a node
 *   reporting without forgetting it, disable it.
 *
 * Roles are enforced by the server — editing needs operator, deleting needs
 * owner. The controls are shown to everyone and the 403 explains itself, rather
 * than a missing button being mistaken for a missing feature.
 */

import { lazy, Suspense, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import {
  Checkbox,
  Select,
  SelectItem,
  SelectItemGroup,
  TextInput,
} from '../components/ui/Form';
import { TerminalIcon } from '../components/ui/Icon';

import { Badge, type Tone } from '../components/Badge';
import { Callout } from '../components/Callout';
import {
  ADDRESSES_HIDDEN_TEXT,
  CONFLICT_TEXT,
  CONTAINER_TEXT,
  MeshSourceBadge,
} from '../components/EdgeAddresses';
import { EdgeNodeUploadsCard } from '../components/reports/EdgeUploadsSection';
import { Freshness } from '../components/Freshness';
import { PageHeader } from '../components/PageHeader';
import { ApiError, apiDelete, apiPost, apiPut, describeError } from '../lib/api';
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelative,
  UNKNOWN,
} from '../lib/format';
import { useResource, type ResourceState } from '../lib/useResource';
import { exitLabel, RecordingBadge } from './TerminalSessionsPage';
import type {
  CenterListResponse,
  EdgeAccessResponse,
  EdgeLinkRequest,
  EdgeNodeDeleteResponse,
  EdgeNodeResponse,
  EdgePeerSuggestion,
  HostKeyScanResponse,
  HostKeyTrustRequest,
  TerminalSessionListResponse,
} from '../types/api';

import '../styles/page-terminal.css';

// xterm is ~300 KB; it belongs only on the one page that opens a terminal, so it
// is a lazily-loaded chunk rather than weight in every page's initial bundle.
const LiveTerminal = lazy(() =>
  import('../components/LiveTerminal').then((module) => ({
    default: module.LiveTerminal,
  })),
);

const REFRESH_MS = 30_000;

const LIVENESS_TONE: Record<string, Tone> = {
  online: 'good',
  stale: 'warning',
  offline: 'critical',
  never: 'neutral',
};

function Row({ term, children }: { term: string; children: ReactNode }) {
  return (
    <>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </>
  );
}

/**
 * The editable state of a node. Mounted only once its node has loaded, and
 * keyed by uuid upstream, so its initial state is taken from stable props
 * rather than synced from them on every render.
 */
function ManageForm({
  node,
  onSaved,
}: {
  node: EdgeNodeResponse;
  onSaved: (fresh: EdgeNodeResponse) => void;
}) {
  const centers = useResource<CenterListResponse>('/api/centers');

  const [label, setLabel] = useState(node.label ?? '');
  const [hospital, setHospital] = useState(node.hospital ?? '');
  const [centerSlug, setCenterSlug] = useState(node.center_slug ?? '');
  const [status, setStatus] = useState<'active' | 'disabled'>(
    node.status === 'disabled' ? 'disabled' : 'active',
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // The node carries a doctor id but no center resolved for it — an assignment
  // to a center that is not in this fleet's config. The select cannot show it,
  // so it is called out rather than silently dropped on the next save.
  const orphanDoctor = node.center_slug === null && node.doctor_id !== null;

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const fresh = await apiPut<EdgeNodeResponse>(
        `/api/edge-nodes/${encodeURIComponent(node.node_uuid)}`,
        {
          label: label.trim() || null,
          hospital: hospital.trim() || null,
          center_slug: centerSlug || null,
          status,
        },
      );
      setSaved(true);
      onSaved(fresh);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  const willDisable = status === 'disabled' && node.status !== 'disabled';

  return (
    <Card aria-labelledby="manage-heading">
      <div className="card__header">
        <h2 className="section__title" id="manage-heading">
          Manage
        </h2>
      </div>
      <div className="card__body stack">
        {error ? (
          <Callout tone="critical" title="Could not save">
            {error}
          </Callout>
        ) : null}

        {saved && !error ? (
          <Callout tone="good" title="Saved">
            The registry is updated. Changes here take effect immediately — there
            is no plan to apply.
          </Callout>
        ) : null}

        {orphanDoctor ? (
          <Callout tone="warning" title="Assigned to a center not in this fleet">
            This node reports <code>doctor_id {node.doctor_id}</code>, which no
            center in the current configuration claims. Choosing a center below and
            saving will reassign it; leaving the selector as “Unlinked” and saving
            will clear the assignment.
          </Callout>
        ) : null}

        <TextInput
          id="manage-label"
          labelText="Label"
          helperText="A human name for this node. The node reports one at enrolment; this overrides it."
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="City Hospital basement node"
        />

        <TextInput
          id="manage-hospital"
          labelText="Hospital"
          value={hospital}
          onChange={(event) => setHospital(event.target.value)}
          placeholder="City Hospital"
        />

        <Select
          id="manage-center"
          labelText="Center assignment"
          helperText="Which cloud center this node replicates to. Resolved to the center’s doctor id (Contract A). Unlinking drops it back to matching on the cloud peer hostname at the next heartbeat."
          value={centerSlug}
          onChange={(event) => setCenterSlug(event.target.value)}
        >
          <SelectItem value="" text="Unlinked — link from cloud peer hostname" />
          {(centers.data?.centers ?? []).map((center) => (
            <SelectItem
              key={center.slug}
              value={center.slug}
              text={`${center.slug} — ${center.domain}`}
            />
          ))}
        </Select>

        <div className="field">
          <span className="field__label">Status</span>
          <div className="row" role="group" aria-label="Node status">
            <Button
              type="button"
              size="sm"
              kind={status === 'active' ? 'primary' : 'tertiary'}
              onClick={() => setStatus('active')}
              aria-pressed={status === 'active'}
            >
              Active
            </Button>
            <Button
              type="button"
              size="sm"
              kind={status === 'disabled' ? 'danger' : 'tertiary'}
              onClick={() => setStatus('disabled')}
              aria-pressed={status === 'disabled'}
            >
              Disabled
            </Button>
          </div>
          <span className="field__help">
            A disabled node’s heartbeats are rejected at the ingest with a 403 — it
            stops reporting until re-enabled. It is not deleted, and its history is
            kept.
          </span>
        </div>

        {willDisable ? (
          <Callout tone="warning" title="This will disable the node on save">
            The next heartbeat from <code>{node.label ?? node.node_uuid}</code> will
            be refused. Nothing else about the node changes, and it can be
            re-enabled here at any time.
          </Callout>
        ) : null}

        <div className="toolbar">
          <Button type="button" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Forget a node entirely. Two-step, owner-only on the server. The confirm
 * phrase is the node's uuid, because a label can be blank or duplicated and a
 * destructive action should name exactly one row.
 */
function DeletePanel({ node }: { node: EdgeNodeResponse }) {
  const navigate = useNavigate();
  const [armed, setArmed] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await apiDelete<EdgeNodeDeleteResponse>(
        `/api/edge-nodes/${encodeURIComponent(node.node_uuid)}`,
      );
      navigate('/edge-nodes');
    } catch (cause) {
      setError(describeError(cause));
      setBusy(false);
    }
  }

  const confirmed = confirm.trim() === node.node_uuid;

  return (
    <Card aria-labelledby="delete-heading">
      <div className="card__header">
        <h2 className="section__title" id="delete-heading">
          Delete
        </h2>
      </div>
      <div className="card__body stack">
        {error ? (
          <Callout tone="critical" title="Could not delete">
            {error}
          </Callout>
        ) : null}

        {armed ? (
          <>
            <Callout tone="warning" title="Deleting is not the same as disabling">
              This forgets the node and its heartbeat history. A node still running
              in the hospital will re-enrol with a fresh token and reappear — to
              stop it reporting without forgetting it, disable it instead. Owner
              role required.
            </Callout>
            <TextInput
              id="delete-confirm"
              className="mono"
              labelText={
                <>
                  Type <code>{node.node_uuid}</code> to confirm
                </>
              }
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <div className="toolbar">
              <Button
                type="button"
                kind="danger"
                onClick={remove}
                disabled={!confirmed || busy}
              >
                {busy ? 'Deleting…' : 'Forget this node'}
              </Button>
              <Button
                type="button"
                kind="tertiary"
                onClick={() => {
                  setArmed(false);
                  setConfirm('');
                }}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="muted">
              Removes this node from the registry, along with its heartbeat
              history. Its telemetry stops appearing in the fleet view. Owner role
              required.
            </p>
            <div>
              <Button type="button" kind="tertiary" onClick={() => setArmed(true)}>
                Delete node…
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Shell access to the node: where it would connect, which key proves it is this
 * node, and the button — rebuilt on `GET /access`, which the SERVER computes.
 *
 * Nothing here lets the browser choose an address. The server derives it (the
 * owner's NetBird link, else the agent's report) and refuses, as data, every
 * case where it cannot be sure; this card renders that decision and offers the
 * two owner actions that resolve most refusals:
 *
 * * **Link to a NetBird peer** — for a node whose agent does not report its
 *   mesh address yet, or reports one that disagrees. Suggested peers come
 *   first with the reason they are suggested; a peer already linked to another
 *   node is listed but cannot be picked.
 * * **Trust the host key** — fetch the key the node presents (a key exchange,
 *   no login), then compare its fingerprint out of band before trusting it. The
 *   server re-scans on Trust and stores the key only if the node presents the
 *   same fingerprint again, so a stale confirm cannot pin the wrong key.
 *
 * Both work while the terminal kill switch is off, so every node can be linked
 * and pinned before anyone can open a shell.
 *
 * The recording notice is not small print: every keystroke, passwords
 * included, and all output are recorded, and on a hospital box the output can
 * be patient data. It sits directly above the button that starts one.
 *
 * Owner-only on the server. A non-owner sees the 403 explain itself rather
 * than a blank card.
 */
function TerminalSection({ node }: { node: EdgeNodeResponse }) {
  const base = `/api/edge-nodes/${encodeURIComponent(node.node_uuid)}`;
  const access = useResource<EdgeAccessResponse>(`${base}/access`);
  const d = access.data;
  const recent = useResource<TerminalSessionListResponse>(
    `/api/terminal/sessions?node_uuid=${encodeURIComponent(node.node_uuid)}&days=365&limit=5`,
    { enabled: d !== null },
  );

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const forbidden = access.error instanceof ApiError && access.error.status === 403;

  /** Every owner action returns (or implies) a new access state; the card
   *  re-reads it rather than patching local state, so what is shown is always
   *  what the server would decide now. */
  async function act(label: string, run: () => Promise<unknown>): Promise<boolean> {
    setBusy(label);
    setActionError(null);
    try {
      await run();
      access.reload();
      return true;
    } catch (cause) {
      setActionError(describeError(cause));
      return false;
    } finally {
      setBusy(null);
    }
  }

  const canOpen = Boolean(
    d && d.ready && d.address && d.terminal.enabled && d.terminal.problems.length === 0,
  );

  return (
    <Card className="masonry-wide" aria-labelledby="terminal-heading">
      <div className="card__header">
        <h2 className="section__title" id="terminal-heading">
          Terminal
        </h2>
        <span className="section__count">recorded</span>
      </div>
      <div className="card__body stack">
        {forbidden ? (
          <Callout tone="warning" title="Owner role required">
            Where a node&rsquo;s shell is, its host key and the terminal itself are
            owner-only. Edge log streaming (Logs) needs only the operator role.
          </Callout>
        ) : access.loading && !d ? (
          <p className="muted">Working out how to reach this node…</p>
        ) : !d ? (
          <Callout tone="critical" title="Could not work out how to reach this node">
            {describeError(access.error)}{' '}
            <Button kind="ghost" size="sm" onClick={access.reload}>
              Try again
            </Button>
          </Callout>
        ) : open && canOpen ? (
          <Suspense fallback={<p className="muted">Loading terminal…</p>}>
            <LiveTerminal
              nodeUuid={node.node_uuid}
              expectedAddress={d.address}
              onClose={() => {
                setOpen(false);
                access.reload();
                recent.reload();
              }}
            />
          </Suspense>
        ) : (
          <>
            {actionError ? (
              <Callout tone="critical" title="That did not work">
                {actionError}
              </Callout>
            ) : null}

            <TerminalSwitchNotice access={d} />

            <ReachRows
              access={d}
              busy={busy}
              onUnlink={() => act('unlink', () => apiDelete<EdgeAccessResponse>(`${base}/link`))}
            />

            {d.warnings.length > 0 ? (
              <Callout tone="warning" title="Worth checking">
                <ul className="terminal-list">
                  {d.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </Callout>
            ) : null}

            {canPickPeer(d) ? (
              <LinkPeerForm
                key={d.link?.peer_id ?? 'unlinked'}
                access={d}
                busy={busy}
                onLink={(peerId) =>
                  act('link', () =>
                    apiPut<EdgeAccessResponse>(`${base}/link`, {
                      netbird_peer_id: peerId,
                    } satisfies EdgeLinkRequest),
                  )
                }
              />
            ) : null}

            <HostKeyPanel
              access={d}
              base={base}
              busy={busy}
              act={act}
            />

            <Callout tone="warning" title="Sessions are recorded">
              Every keystroke (including passwords) and all output, encrypted, kept{' '}
              {d.recording_retention_days} days, owner-only. This box may hold patient
              data — look only at what the task needs.
            </Callout>

            <div className="toolbar">
              <Button
                type="button"
                renderIcon={TerminalIcon}
                onClick={() => setOpen(true)}
                disabled={!canOpen || busy !== null}
              >
                {d.address
                  ? `Open terminal as ${d.ssh_user}@${d.address}`
                  : 'Open terminal'}
              </Button>
            </div>
            {!d.ready ? (
              <p className="muted">
                {d.reason || 'Not reachable yet.'}
                {d.reason_code && REASON_HELP[d.reason_code]
                  ? ` ${REASON_HELP[d.reason_code]}`
                  : ''}
              </p>
            ) : null}

            <RecentSessions nodeUuid={node.node_uuid} recent={recent} />
          </>
        )}
      </div>
    </Card>
  );
}

/** What to do about a refusal. The server's `reason` says what is wrong; this
 *  says where on this card it is fixed. */
const REASON_HELP: Record<string, string> = {
  no_address:
    'Link this node to its NetBird peer above — or update its agent to 1.1.0, which reports the mesh address itself.',
  linked_peer_missing:
    'The linked peer is gone from NetBird; the node probably re-joined as a new peer. Link the new one.',
  not_a_peer:
    'The address the agent reports is not a NetBird peer. Link the right peer above.',
  outside_mesh: 'That address is outside the mesh range, so the panel will not SSH to it.',
  not_edge_peer: 'The peer is not in the NetBird edge group; add it there first.',
  peer_offline: 'The peer is not connected to the mesh right now — check the node’s NetBird client.',
  peer_claimed: 'Another node already claims this peer; resolve it on that node first.',
  conflict:
    'The owner link and the agent point at different peers. Re-link to the right one, or check whether the node was reinstalled.',
  no_host_key: 'Fetch the host key below and confirm its fingerprint.',
  host_key_changed: 'See Host key, below.',
};

/** Linking is offered when there is no link, or when the link is the problem. */
function canPickPeer(d: EdgeAccessResponse): boolean {
  const code = d.reason_code;
  if (
    code === 'netbird_unconfigured' ||
    code === 'netbird_unavailable' ||
    code === 'registry_unavailable' ||
    code === 'links_unavailable' ||
    code === 'not_found'
  ) {
    return false;
  }
  return (
    !d.link ||
    code === 'linked_peer_missing' ||
    code === 'conflict' ||
    code === 'not_edge_peer'
  );
}

function TerminalSwitchNotice({ access: d }: { access: EdgeAccessResponse }) {
  if (d.reason_code === 'netbird_unconfigured') {
    return (
      <Callout tone="info" title="NetBird is not configured">
        Set <code>FLEET_NETBIRD_API_URL</code> and <code>FLEET_NETBIRD_API_TOKEN</code>{' '}
        on the controller to reach nodes over the mesh. Until then there is no SSH
        path to this node from here.
      </Callout>
    );
  }
  if (!d.terminal.enabled) {
    return (
      <Callout tone="info" title="The in-browser terminal is switched off">
        Linking this node and trusting its host key still work, so it can be
        prepared now. Opening a shell waits for <code>FLEET_TERMINAL_ENABLED</code> —
        the same deliberate switch as applying.
      </Callout>
    );
  }
  if (d.terminal.problems.length > 0) {
    return (
      <Callout tone="warning" title="The terminal is on but not ready">
        <ul className="terminal-list">
          {d.terminal.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </Callout>
    );
  }
  return null;
}

function ReachRows({
  access: d,
  busy,
  onUnlink,
}: {
  access: EdgeAccessResponse;
  busy: string | null;
  onUnlink: () => Promise<boolean>;
}) {
  const [armed, setArmed] = useState(false);
  return (
    <dl className="dl">
      <Row term="Connects to">
        {d.address ? (
          <>
            <span className="mono">
              {d.ssh_user}@{d.address}
            </span>{' '}
            <MeshSourceBadge source={d.address_source} />
          </>
        ) : (
          <span className="muted">no address — not reachable</span>
        )}
      </Row>
      <Row term="NetBird peer">
        {d.peer ? (
          <>
            {d.peer.name || d.peer.hostname || d.peer.id}{' '}
            {d.peer.connected ? (
              <Badge tone="good" small>
                connected
              </Badge>
            ) : (
              <Badge tone="warning" small>
                offline
              </Badge>
            )}
            {d.peer.last_seen && !d.peer.connected ? (
              <span className="muted"> last seen {formatRelative(d.peer.last_seen)}</span>
            ) : null}
          </>
        ) : (
          <span className="muted">none resolved</span>
        )}
      </Row>
      <Row term="Agent reports">
        {d.agent.mesh_ip ? (
          <span className="mono">{d.agent.mesh_ip}</span>
        ) : (
          <span className="muted">no mesh address (agent before 1.1.0?)</span>
        )}
      </Row>
      <Row term="Owner link">
        {d.link ? (
          <div className="terminal-link">
            <span>
              {d.link.peer_name || d.link.peer_id}
              {d.link.linked_by ? (
                <span className="muted">
                  {' '}
                  by {d.link.linked_by}
                  {d.link.linked_at ? `, ${formatRelative(d.link.linked_at)}` : ''}
                </span>
              ) : null}
            </span>
            {armed ? (
              <div className="row">
                <span className="muted">
                  Unlinking keeps the pinned host key, but linking any peer afterwards,
                  even the same one, clears it; forget it under Host key if the
                  machine changed.
                </span>
                <Button
                  size="sm"
                  kind="danger"
                  disabled={busy !== null}
                  onClick={async () => {
                    if (await onUnlink()) setArmed(false);
                  }}
                >
                  {busy === 'unlink' ? 'Unlinking…' : 'Unlink'}
                </Button>
                <Button size="sm" kind="tertiary" onClick={() => setArmed(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div>
                <Button size="sm" kind="ghost" onClick={() => setArmed(true)}>
                  Unlink…
                </Button>
              </div>
            )}
          </div>
        ) : (
          <span className="muted">not linked</span>
        )}
      </Row>
    </dl>
  );
}

/** A peer held by another, still-registered node, outside the mesh range, or
 *  outside the NetBird edge group cannot be picked. Nor can any peer other
 *  than the agent-reported one when the node's agent reports a mesh address
 *  that belongs to a live peer (`agentBound`): PUT /link refuses those with
 *  409. A link left behind by a deleted node does not block: the server
 *  releases it when this node links the peer. */
function peerBlocked(s: EdgePeerSuggestion, agentBound: boolean): boolean {
  return (
    (Boolean(s.linked_to) && !s.linked_to_deleted) ||
    s.in_mesh === false ||
    s.in_edge_group === false ||
    (agentBound && s.reasons.length === 0)
  );
}

function peerText(s: EdgePeerSuggestion, agentBound: boolean): string {
  const p = s.peer;
  const name = p.name || p.hostname || p.dns_label || p.id;
  const parts = [`${name} — ${p.ip || 'no address'}`];
  if (!p.connected) parts.push('(offline)');
  if (s.linked_to && s.linked_to_deleted) {
    parts.push(`(held by deleted node ${s.linked_to} — linking releases it)`);
  } else if (s.linked_to) {
    parts.push(`(linked to ${s.linked_to})`);
  }
  if (s.in_mesh === false) parts.push('(outside the mesh range)');
  if (s.in_edge_group === false) parts.push('(not in the edge-node group)');
  if (agentBound && s.reasons.length === 0) {
    parts.push('(the node’s agent reports another peer’s address)');
  }
  return parts.join(' ');
}

function LinkPeerForm({
  access: d,
  busy,
  onLink,
}: {
  access: EdgeAccessResponse;
  busy: string | null;
  onLink: (peerId: string) => Promise<boolean>;
}) {
  const suggested = d.suggestions.filter((s) => s.reasons.length > 0);
  const others = d.suggestions.filter((s) => s.reasons.length === 0);
  // `reasons` is non-empty only for the agent-ip match: when the agent reports
  // a live peer's mesh address, the server links no other peer.
  const agentBound = d.suggestions.some((s) => s.reasons.length > 0);
  // Pre-select the single pickable suggestion: the common case (the agent
  // reports an address that is exactly one peer) becomes one click.
  const pickable = suggested.filter((s) => !peerBlocked(s, agentBound));
  const [peerId, setPeerId] = useState(pickable.length === 1 ? pickable[0]?.peer.id ?? '' : '');
  const chosen = d.suggestions.find((s) => s.peer.id === peerId) ?? null;

  if (d.suggestions.length === 0) {
    return (
      <p className="muted">
        NetBird lists no peers to link this node to. Enrol the node in the mesh
        first (a setup key from the Edge Nodes page).
      </p>
    );
  }

  return (
    <div className="stack">
      <Select
        id="terminal-link-peer"
        labelText={d.link ? 'Re-link to NetBird peer' : 'Link to NetBird peer'}
        helperText="An owner’s link decides the address. When the node’s agent reports a mesh address, only that address’s peer can be linked."
        value={peerId}
        onChange={(event) => setPeerId(event.target.value)}
        disabled={busy !== null}
      >
        <SelectItem value="" text="Select a peer…" />
        {suggested.length > 0 ? (
          <SelectItemGroup label="Suggested">
            {suggested.map((s) => (
              <SelectItem
                key={s.peer.id}
                value={s.peer.id}
                text={peerText(s, agentBound)}
                disabled={peerBlocked(s, agentBound)}
              />
            ))}
          </SelectItemGroup>
        ) : null}
        {others.length > 0 ? (
          <SelectItemGroup label="All peers">
            {others.map((s) => (
              <SelectItem
                key={s.peer.id}
                value={s.peer.id}
                text={peerText(s, agentBound)}
                disabled={peerBlocked(s, agentBound)}
              />
            ))}
          </SelectItemGroup>
        ) : null}
      </Select>
      {chosen && chosen.reasons.length > 0 ? (
        <p className="muted">Suggested because {chosen.reasons.join('; ')}.</p>
      ) : null}
      {/* The server clears the pin whenever the stored peer differs from the
          new one, a never-linked or unlinked node (stored peer NULL) included. */}
      {d.host_key.pinned && peerId !== '' && peerId !== (d.link?.peer_id ?? null) ? (
        <Callout tone="warning" title="The pinned host key will be cleared">
          {d.link
            ? 'Linking a different peer clears the pinned host key; it must be trusted again.'
            : 'This node is not linked, so linking any peer clears the pinned host key; it must be trusted again.'}
        </Callout>
      ) : null}
      <div className="toolbar">
        <Button
          size="sm"
          disabled={!peerId || busy !== null}
          onClick={() => void onLink(peerId)}
        >
          {busy === 'link' ? 'Linking…' : 'Link'}
        </Button>
      </div>
    </div>
  );
}

const SSH_KEYGEN_HINT = 'ssh-keygen -lf /etc/ssh/ssh_host_ed25519_key.pub';

function HostKeyPanel({
  access: d,
  base,
  busy,
  act,
}: {
  access: EdgeAccessResponse;
  base: string;
  busy: string | null;
  act: (label: string, run: () => Promise<unknown>) => Promise<boolean>;
}) {
  const hk = d.host_key;
  const [scan, setScan] = useState<HostKeyScanResponse | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [acknowledge, setAcknowledge] = useState(false);
  const [forgetArmed, setForgetArmed] = useState(false);

  const changed = d.reason_code === 'host_key_changed' || hk.agrees_with_agent === false;
  // A scan needs an address the server trusts; only the KEY may be in doubt.
  const canScan =
    Boolean(d.address) &&
    (d.ready || d.reason_code === 'no_host_key' || d.reason_code === 'host_key_changed');

  async function fetchKey() {
    setScanning(true);
    setScanError(null);
    setScan(null);
    setAcknowledge(false);
    try {
      setScan(await apiPost<HostKeyScanResponse>(`${base}/host-key/scan`));
    } catch (cause) {
      setScanError(describeError(cause));
    } finally {
      setScanning(false);
    }
  }

  return (
    <div className="stack">
      <h3 className="section__subtitle">Host key</h3>

      {hk.pinned ? (
        <>
          <span className="hostkey-fp">{hk.fingerprint}</span>
          <p className="muted">
            {hk.key_type ? `${hk.key_type}, ` : ''}
            {hk.source === 'owner-confirmed'
              ? 'confirmed by an owner'
              : hk.source === 'agent'
                ? 'reported by the agent and pinned on the first verified connect'
                : hk.source ?? 'pinned'}
            {hk.pinned_by ? ` (${hk.pinned_by}` : ''}
            {hk.pinned_by && hk.pinned_at ? `, ${formatRelative(hk.pinned_at)})` : hk.pinned_by ? ')' : ''}
            . The terminal and edge logs accept this key and no other.
          </p>
        </>
      ) : hk.agent_fingerprint && d.ready ? (
        <p className="muted">
          Not pinned yet. The agent reports <span className="mono">{hk.agent_fingerprint}</span>;
          it is pinned on the first connection that verifies it. To pin it now,
          fetch and confirm it below.
        </p>
      ) : (
        <p className="muted">No host key is pinned for this node.</p>
      )}

      {changed ? (
        <Callout tone="critical" title="The node’s host key has changed" live>
          <p>
            Pinned: <span className="mono">{hk.fingerprint ?? UNKNOWN}</span>
            <br />
            Agent now reports: <span className="mono">{hk.agent_fingerprint ?? UNKNOWN}</span>
          </p>
          <p>
            Either the node was rebuilt (a new key is expected) or something else
            is answering at its address. Confirm with the hospital before
            forgetting the old key and trusting a new one.
          </p>
        </Callout>
      ) : null}

      {hk.pinned ? (
        forgetArmed ? (
          <div className="row">
            <span className="muted">
              Forgetting the pin blocks the terminal until a key is trusted again.
            </span>
            <Button
              size="sm"
              kind="danger"
              disabled={busy !== null}
              onClick={async () => {
                if (await act('forget', () => apiDelete<EdgeAccessResponse>(`${base}/host-key`))) {
                  setForgetArmed(false);
                }
              }}
            >
              {busy === 'forget' ? 'Forgetting…' : 'Forget pinned key'}
            </Button>
            <Button size="sm" kind="tertiary" onClick={() => setForgetArmed(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <div>
            <Button size="sm" kind={changed ? 'secondary' : 'ghost'} onClick={() => setForgetArmed(true)}>
              Forget pinned key…
            </Button>
          </div>
        )
      ) : scan ? (
        <div className="stack">
          <Callout tone="info" title="Confirm this fingerprint before trusting it">
            <p>The node at <span className="mono">{scan.address}</span> presents:</p>
            <span className="hostkey-fp">
              {scan.key_type} {scan.fingerprint}
            </span>
            <p>
              {scan.matches_agent === true
                ? 'It matches the key the node’s agent reports.'
                : scan.matches_agent === false
                  ? `It does NOT match the key the agent reports (${scan.agent_fingerprint ?? UNKNOWN}).`
                  : 'The agent does not report a key to compare with.'}
            </p>
            <p>
              Compare it out of band — at the node&rsquo;s console, or read out by the
              hospital&rsquo;s IT — with the output of{' '}
              <code>{SSH_KEYGEN_HINT}</code>. A match here only proves what
              answered at this address, not that it is the right machine.
            </p>
          </Callout>
          {scan.matches_agent === false ? (
            <Checkbox
              id="hostkey-ack"
              labelText="I confirmed this fingerprint on the node itself; trust it even though the agent disagrees."
              checked={acknowledge}
              onChange={(_event, { checked }) => setAcknowledge(checked)}
            />
          ) : null}
          <div className="toolbar">
            <Button
              size="sm"
              disabled={busy !== null || (scan.matches_agent === false && !acknowledge)}
              onClick={async () => {
                const body: HostKeyTrustRequest = {
                  fingerprint: scan.fingerprint,
                  acknowledge_agent_mismatch: acknowledge,
                };
                if (await act('trust', () => apiPost<EdgeAccessResponse>(`${base}/host-key/trust`, body))) {
                  setScan(null);
                }
              }}
            >
              {busy === 'trust' ? 'Trusting…' : 'Trust this key'}
            </Button>
            <Button size="sm" kind="tertiary" onClick={() => setScan(null)} disabled={busy !== null}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          {scanError ? (
            <Callout tone="critical" title="Could not fetch the host key">
              {scanError}
            </Callout>
          ) : null}
          <div>
            <Button
              size="sm"
              kind="secondary"
              onClick={fetchKey}
              disabled={!canScan || scanning || busy !== null}
              title={
                canScan
                  ? 'Runs the SSH key exchange only — no login is attempted.'
                  : 'The node needs a reachable mesh address first.'
              }
            >
              {scanning ? 'Fetching…' : 'Fetch host key'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function RecentSessions({
  nodeUuid,
  recent,
}: {
  nodeUuid: string;
  recent: ResourceState<TerminalSessionListResponse>;
}) {
  if (recent.error && !recent.data) {
    return (
      <p className="muted">
        Recent sessions could not be listed: {describeError(recent.error)}
      </p>
    );
  }
  const rows = recent.data?.sessions ?? [];
  // The server reports an unreadable session store as data: an empty list
  // with a reason, which is not the same as "nobody opened a shell".
  const unreadable = recent.data?.reason ?? '';
  return (
    <div className="stack">
      <h3 className="section__subtitle">Recent sessions</h3>
      {recent.loading && !recent.data ? (
        <p className="muted">Loading…</p>
      ) : unreadable ? (
        <p className="muted">Recent sessions could not be listed: {unreadable}</p>
      ) : rows.length === 0 ? (
        <p className="muted">No shell has been opened on this node from the panel.</p>
      ) : (
        <ul className="terminal-recent">
          {rows.slice(0, 5).map((s) => (
            <li key={s.id}>
              <Link to={`/terminal-sessions/${s.id}`} title={formatDateTime(s.started_at)}>
                #{s.id} · {formatRelative(s.started_at)}
              </Link>
              <span className="muted">{s.actor_email}</span>
              <span className="muted">{s.ended_at ? formatDuration(s.duration_s) : 'open'}</span>
              <Badge tone="neutral" small>
                {exitLabel(s.exit_reason)}
              </Badge>
              <RecordingBadge state={s.recording_state} />
            </li>
          ))}
        </ul>
      )}
      <div>
        <Link to={`/terminal-sessions?node=${encodeURIComponent(nodeUuid)}&days=365`}>
          All sessions on this node
        </Link>
      </div>
    </div>
  );
}

export function EdgeNodeDetailPage() {
  const { uuid = '' } = useParams<{ uuid: string }>();
  const node = useResource<EdgeNodeResponse>(
    `/api/edge-nodes/${encodeURIComponent(uuid)}`,
    { enabled: uuid.length > 0, refreshMs: REFRESH_MS },
  );

  const data = node.data;

  return (
    <>
      <PageHeader
        title={data?.label ?? uuid}
        subtitle={
          data?.hospital ??
          'An on-premise hospital node, as its agent last reported.'
        }
        actions={
          <>
            {data ? (
              <Badge tone={LIVENESS_TONE[data.liveness] ?? 'neutral'}>
                {data.liveness}
              </Badge>
            ) : null}
            <Freshness
              checkedAt={node.fetchedAt}
              label="Fetched"
              refreshing={node.refreshing}
              onRefresh={node.reload}
            />
            <Link to="/edge-nodes" className="btn btn--tertiary btn--sm">
              All nodes
            </Link>
          </>
        }
      />

      {node.error && !data ? (
        <Callout tone="critical" title="Could not load this node">
          {describeError(node.error)}{' '}
          <Link to="/edge-nodes">Back to all nodes</Link>.
        </Callout>
      ) : !data ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="masonry">
          {/* ── telemetry ─────────────────────────────────────────────────── */}
          <Card aria-labelledby="telemetry-heading">
            <div className="card__header">
              <h2 className="section__title" id="telemetry-heading">
                Telemetry
              </h2>
              <span className="section__count">counts and versions only</span>
            </div>
            <div className="card__body stack">
              <dl className="dl">
                <Row term="Node UUID">
                  <span className="mono">{data.node_uuid}</span>
                </Row>
                <Row term="Center">
                  {data.center_slug ? (
                    <Link to={`/centers/${data.center_slug}`}>
                      {data.center_slug}
                    </Link>
                  ) : data.doctor_id ? (
                    <span className="muted">doctor {data.doctor_id} (no center)</span>
                  ) : (
                    <Badge tone="warning">unlinked</Badge>
                  )}
                </Row>
                <Row term="Cloud peer host">
                  {data.cloud_peer_host ? (
                    <span className="mono">{data.cloud_peer_host}</span>
                  ) : (
                    <span className="muted">{UNKNOWN}</span>
                  )}
                </Row>
                <Row term="Liveness">
                  <Badge tone={LIVENESS_TONE[data.liveness] ?? 'neutral'}>
                    {data.liveness}
                  </Badge>
                </Row>
                <Row term="Last seen">
                  {data.last_seen_at ? (
                    <>
                      {formatRelative(data.last_seen_at)}{' '}
                      <span className="muted">
                        ({formatDateTime(data.last_seen_at)})
                      </span>
                    </>
                  ) : (
                    <span className="muted">never</span>
                  )}
                </Row>
                <Row term="Orthanc">
                  {data.orthanc_up === null ? (
                    <span className="muted">{UNKNOWN}</span>
                  ) : data.orthanc_up ? (
                    <Badge tone="good">up {data.orthanc_version ?? ''}</Badge>
                  ) : (
                    <Badge tone="critical">down</Badge>
                  )}
                </Row>
                <Row term="Studies">{formatNumber(data.studies_count)}</Row>
                <Row term="Instances">{formatNumber(data.instances_count)}</Row>
                <Row term="Unreplicated">
                  {data.unreplicated_count === null ? (
                    <span className="muted">{UNKNOWN}</span>
                  ) : data.unreplicated_count > 0 ? (
                    <strong>{formatNumber(data.unreplicated_count)}</strong>
                  ) : (
                    0
                  )}
                </Row>
                <Row term="Disk free">
                  {formatBytes(data.disk_free_bytes)}
                  {data.disk_total_bytes ? (
                    <span className="muted"> of {formatBytes(data.disk_total_bytes)}</span>
                  ) : null}
                </Row>
                <Row term="Last replication">
                  {/* Sent by agent 1.1.0 onwards; an older agent never
                      reported it, which is not the same as never replicating. */}
                  {data.last_replication_success_at ? (
                    <span title={formatDateTime(data.last_replication_success_at)}>
                      {formatRelative(data.last_replication_success_at)}
                    </span>
                  ) : (
                    <span className="muted">not reported</span>
                  )}
                </Row>
                <Row term="Uptime">
                  {data.uptime_s !== null ? (
                    formatDuration(data.uptime_s)
                  ) : (
                    <span className="muted">not reported by this agent</span>
                  )}
                </Row>
                {/* Addresses are operator-only; the server withholds them from a
                    viewer, and one "Hidden" row says so instead of four rows
                    of "not reported" that would claim the node is silent. */}
                {data.addresses_hidden ? (
                  <Row term="Addresses">
                    <span className="muted">{ADDRESSES_HIDDEN_TEXT}</span>
                  </Row>
                ) : (
                  <>
                    <Row term="LAN">
                      {data.private_ip ? (
                        <>
                          <span className="mono">{data.private_ip}</span>
                          {data.private_ip_is_container ? (
                            <>
                              {' '}
                              <Badge tone="warning" small title={CONTAINER_TEXT}>
                                container address
                              </Badge>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <span className="muted">not reported</span>
                      )}
                    </Row>
                    <Row term="Public">
                      {data.public_ip ? (
                        <span
                          className="mono"
                          title="Observed by the ingest from the connection, never self-reported."
                        >
                          {data.public_ip}
                        </span>
                      ) : (
                        <span className="muted">not observed</span>
                      )}
                    </Row>
                    <Row term="Mesh">
                      {data.mesh_ip ? (
                        <>
                          <span className="mono">{data.mesh_ip}</span>{' '}
                          <MeshSourceBadge source={data.mesh_ip_source} />
                          {data.mesh_ip_conflict ? (
                            <>
                              {' '}
                              <Badge tone="warning" small title={CONFLICT_TEXT}>
                                conflict
                              </Badge>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <span className="muted">
                          {data.netbird_peer_id
                            ? 'linked peer has no address yet'
                            : 'not reported, not linked'}
                        </span>
                      )}
                    </Row>
                    <Row term="SSH host key">
                      {data.ssh_host_key_fingerprint ? (
                        <span
                          className="mono"
                          title="SHA-256 fingerprint of the host key the agent reported. The key itself never leaves the server."
                        >
                          {data.ssh_host_key_fingerprint}
                        </span>
                      ) : (
                        <span className="muted">not reported by this agent</span>
                      )}
                    </Row>
                  </>
                )}
                <Row term="Auto-update">
                  {data.auto_update === null ? (
                    <span className="muted">not reported</span>
                  ) : data.auto_update ? (
                    'enabled'
                  ) : (
                    'disabled'
                  )}
                </Row>
                <Row term="Agent version">
                  {data.agent_version ?? <span className="muted">{UNKNOWN}</span>}
                </Row>
                <Row term="Config hash">
                  {data.config_hash ? (
                    <span className="mono" title={data.config_hash}>
                      {data.config_hash.slice(0, 12)}…
                    </span>
                  ) : (
                    <span className="muted">{UNKNOWN}</span>
                  )}
                </Row>
                <Row term="Enrolled">{formatDateTime(data.created_at)}</Row>
              </dl>
            </div>
          </Card>

          <EdgeNodeUploadsCard key={`uploads-${data.node_uuid}`} nodeUuid={data.node_uuid} />

          {/* Keyed by uuid so navigating between nodes resets the form state,
              which React Router would otherwise carry across the param change. */}
          <TerminalSection key={`term-${data.node_uuid}`} node={data} />
          <ManageForm key={data.node_uuid} node={data} onSaved={() => node.reload()} />
          <DeletePanel key={`del-${data.node_uuid}`} node={data} />
        </div>
      )}
    </>
  );
}
